import * as React from "react";
import { HashLink } from "react-router-hash-link";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import styled from "@material-ui/styles/styled";
import useTheme from "@material-ui/core/styles/useTheme";

import AttachMoney from "@material-ui/icons/AttachMoney";
import CheckCircle from "@material-ui/icons/CheckCircle";
import Equalizer from "@material-ui/icons/Equalizer";
import Gavel from "@material-ui/icons/Gavel";
import ShowChart from "@material-ui/icons/ShowChart";
import Spellcheck from "@material-ui/icons/Spellcheck";
import StopScreenShare from "@material-ui/icons/StopScreenShare";
import TrendingFlat from "@material-ui/icons/TrendingFlat";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import VoiceOverOff from "@material-ui/icons/VoiceOverOff";

import green from "@material-ui/core/colors/green";
import lightBlue from "@material-ui/core/colors/lightBlue";
import orange from "@material-ui/core/colors/orange";
import purple from "@material-ui/core/colors/purple";

import { SAMPLE_REPORT_URL, SUPPORT_EMAIL } from "../utils/Constants";
import { logEvent } from "../utils/Analytics";
import { Iris } from "../components/Iris";
import { PageWrapper } from "../components/PageWrapper/PageWrapper";
import { PageHeader } from "../components/PageWrapper/PageHeader";
import { Container } from "../components/PageWrapper/Container";
import { ColoredBandContainer } from "../components/PageWrapper/ColoredBandContainer";
import { SignupButton } from "../components/SignupButton/SignupButton";
import { BusinessCard } from "../components/BusinessCard/BusinessCard";
import { Testimonials } from "../components/Testimonials/Testimonials";
import { Pricing } from "./PricingPage";

import iris_together from "../images/iris_together.png";
import sellability from "../images/sellability.png";
import progression_chart from "../images/progression_chart.png";
import histogram from "../images/histogram.png";
import genre_analysis from "../images/genre_analysis.png";
import hint from "../images/hint.png";
import publisher from "../images/publisher.svg";

const ActionImage = styled("img")({
  height: 160,
  width: "100%",
  borderRadius: "8px",
  objectFit: "contain",
});

export const AuthorPage = () => {
  return (
    <PageWrapper>
      <Container>
        <PageHeader
          overline={
            <>
              <Iris /> For Authors
            </>
          }
          title={
            <>
              Unlock your book's potential with <Iris />
            </>
          }
          isMainHeadline={true}
        />
        <Box my={8}>
          <Testimonials />
        </Box>
      </Container>
      <ColoredBandContainer noGutter={true}>
        <AuthorBenefits />
      </ColoredBandContainer>
      <Container>
        <PublisherPush />
      </Container>
      <ColoredBandContainer dark={true}>
        <GetStarted darkMode={true} />
      </ColoredBandContainer>
      <Container>
        <MachineAndHuman />
      </Container>
      <ColoredBandContainer>
        <ActionableInsights />
      </ColoredBandContainer>
      <Container>
        <BusinessCards />
      </Container>
      <ColoredBandContainer>
        <Privacy />
      </ColoredBandContainer>
      <Container>
        <Box mt={8} mb={16}>
          <PageHeader
            overline="Pricing"
            title="Affordable pricing options"
            subtitle={
              <>
                <Iris /> reports are ~30x more affordable than traditional
                manuscript assessments.
              </>
            }
          />
          <Pricing canExpand={true} mainPrice="subscription" />
        </Box>
        <GetStarted darkMode={false} />
      </Container>
    </PageWrapper>
  );
};

const PublisherPush = () => {
  return (
    <Box my={8}>
      <a id="get_published" />
      <Grid container={true} spacing={4}>
        <Grid
          item={true}
          xs={12}
          md={6}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img src={publisher} style={{ width: "100%", maxWidth: 320 }} />
        </Grid>
        <Grid item={true} xs={12} md={6}>
          <PageHeader
            overline="Our promise to you"
            title="We'll get your manuscript in front of a publisher"
            subtitle="Want to get published? If your manuscript scores high, we'll advocate for you."
            align="left"
          />
          <Typography variant="body1">
            <b>
              If your manuscript scores higher than 80, we'll forward your
              report to a set of publishers in your genre.
            </b>{" "}
            We believe in our reports and want to give new literary voices a
            chance. And with 100+ publishers at our fingertips, we're confident
            to pique somebody's interest.
            <br />
            Not your thing? No worries, you can always opt out.
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const AuthorBenefits = () => {
  const benefits = [
    {
      title: "Sales Assessment",
      subtitle: (
        <>
          <Iris /> predicts the commercial success of your book with 5 dedicated
          metrics like <b>sales potential</b> and <b>reader rating</b>.
        </>
      ),
      to: "/reports#sellability",
      icon: <AttachMoney />,
      color: lightBlue,
    },
    {
      title: "Genre Comparisons",
      subtitle: (
        <>
          <Iris /> visualizes reader expectations and shows how your manuscript{" "}
          <b>compares to bestsellers</b> in your genre. .
        </>
      ),
      to: "/reports#themes",
      icon: <Equalizer />,
      color: purple,
    },
    {
      title: "Highs & Lows",
      subtitle: (
        <>
          <Iris /> identifies <b>strong parts</b> and <b>weak spots</b> that
          might need some work (overall and for individual metrics).
        </>
      ),
      to: "/reports#how_to",
      icon: <Spellcheck />,
      color: orange,
    },
    {
      title: "Style Measurements",
      subtitle: (
        <>
          <Iris /> analyzes 25+ style metrics (reading score, pace, active
          verbs, ...) and provides <b>editorial feedback</b>.
        </>
      ),
      icon: <ShowChart />,
      to: "/reports#style",
      color: green,
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Imagine every author had a co-author..."
        subtitle={
          <>
            ... a co-author who would read every version of your manuscript and
            provide an instant assessment of <b>reader rating</b>,{" "}
            <b>sales potential</b>, and identify parts of the manuscript most
            likely to resonate with readers.
            <br />
            <br />
            Welcome to <Iris />!
          </>
        }
      />
      <Box>
        <Grid container={true} spacing={4} alignItems="stretch">
          {benefits.map((b, i) => (
            <Grid
              item={true}
              xs={12}
              sm={6}
              md={3}
              key={i}
              style={{ display: "flex" }}
            >
              <Card elevation={2} style={{ display: "flex" }}>
                <Box display="flex" flexDirection="column" m={4}>
                  <Box flexGrow={1}>
                    <Box
                      bgcolor={b.color[50]}
                      color={b.color[500]}
                      borderRadius={8}
                      width={24}
                      height={24}
                      p={2}
                      mb={2}
                      textAlign="center"
                    >
                      {b.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom={true}
                      style={{ fontWeight: 700 }}
                    >
                      {b.title}
                    </Typography>
                    <Typography color="textSecondary">{b.subtitle}</Typography>
                  </Box>
                  <Box
                    flexGrow={0}
                    display="flex"
                    justifyContent="flex-end"
                    mt={4}
                  >
                    <Button
                      component={HashLink}
                      to={b.to}
                      color="primary"
                      endIcon={<TrendingFlat />}
                    >
                      Learn more
                    </Button>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

const MachineAndHuman = () => {
  return (
    <Grid container={true} spacing={4}>
      <Grid item={true} container={true} alignItems="center" xs={12} md={6}>
        <Box>
          <PageHeader
            overline="Humans & Machines"
            title="The best of both worlds"
            align="left"
          />
          <Typography variant="body1">
            Ultimately, the combination of both data and human creativity
            maximizes a manuscript's chances of success. Just like a human
            editor, <Iris /> compares manuscripts against what has worked in the
            past and genre norms. It's up to authors to interpret the results
            and assess the opportunities. For example, an author might choose a
            slower pace or longer sentences than typical for their genre.{" "}
            <Iris /> simply makes these metrics visible and gives authors that
            conscious choice.
          </Typography>
        </Box>
      </Grid>
      <Grid item={true} alignItems="center" xs={12} md={6}>
        <img src={iris_together} style={{ width: "100%" }} />
      </Grid>
    </Grid>
  );
};

const GetStarted = (props: { darkMode: boolean }) => {
  const { darkMode } = props;

  return (
    <Box my={2}>
      <PageHeader
        overline="Get Started"
        title={
          <>
            Get started with <Iris /> today
          </>
        }
        subtitle={
          <>
            Join the publishing revolution and be an early adopter of <Iris />.
          </>
        }
        align="center"
        darkMode={darkMode}
      />
      <SignupButton place="author-page" />
    </Box>
  );
};

export const ActionableInsights = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"), {
    defaultMatches: true,
  });

  return (
    <Box my={4}>
      <PageHeader
        overline="Reports"
        title={<>Actionable Insights</>}
        subtitle={
          <>
            Authors can use <Iris />
            's reports in variety of different ways to improve their
            manuscripts.
          </>
        }
        align="center"
      />
      <Grid container={true} spacing={isMd ? 4 : 2}>
        <Grid item={true} xs={12} sm={6} md={4}>
          <Box>
            <ActionImage src={sellability} alt="Sellability Predictions" />
          </Box>
          <Box mt={2}>
            <Typography
              variant="h6"
              gutterBottom={true}
              color="textPrimary"
              style={{ fontWeight: 700 }}
            >
              Sellability Predictions
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Use <b>Manuscript Score</b> and <b>Sales Potential</b> to assess
              the commercial value of your manuscript and whether it's ready for
              pitching.
            </Typography>
          </Box>
          <Box mt={2}>
            <Button
              color="primary"
              component={HashLink}
              to="/reports#sellability"
            >
              Read More
            </Button>
          </Box>
        </Grid>
        <Grid item={true} xs={12} sm={6} md={4}>
          <Box>
            <ActionImage src={genre_analysis} alt="Genre Analysis" />
          </Box>
          <Box mt={2}>
            <Typography
              variant="h6"
              gutterBottom={true}
              color="textPrimary"
              style={{ fontWeight: 700 }}
            >
              Genre Analysis
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Our genre analysis shows, not only how much of each genre your
              manuscript really contains, but also sub-genres that may exist,
              and, therefore, be of importance to specific imprints of trade
              publishers. You can use this to determine whether you hit the
              mark, whether to pitch the work to other imprints, or to tweak
              your work.
            </Typography>
          </Box>
          <Box mt={2}>
            <Button color="primary" component={HashLink} to="/reports#themes">
              Read More
            </Button>
          </Box>
        </Grid>
        <Grid item={true} xs={12} sm={6} md={4}>
          <Box>
            <ActionImage src={histogram} alt="Histogram" />
          </Box>
          <Box mt={2}>
            <Typography
              variant="h6"
              gutterBottom={true}
              color="textPrimary"
              style={{ fontWeight: 700 }}
            >
              Histograms
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Each metric shows its distribution across your entire genre. You
              can use those histograms to figure out how your manuscript stacks
              up against the average and typical bestsellers of each genre.
              Using this knowledge, you can bring your numbers as close as
              possible to the bestseller bucket to{" "}
              <b>improve the commercial value of your manuscript</b>.
            </Typography>
          </Box>
          <Box mt={2}>
            <Button color="primary" component={HashLink} to="/reports#how_to">
              Read More
            </Button>
          </Box>
        </Grid>
        <Grid item={true} xs={12} sm={6} md={4}>
          <Box>
            <ActionImage src={hint} alt="Hints" />
          </Box>
          <Box mt={2}>
            <Typography
              variant="h6"
              gutterBottom={true}
              color="textPrimary"
              style={{ fontWeight: 700 }}
            >
              Editorial Hints
            </Typography>
            <Typography variant="body1" color="textSecondary">
              <Iris /> shows valuable hints to let you know how you're doing for
              each metric. In combination with metric descriptions, you can be
              your own editor and improve your writing!
            </Typography>
          </Box>
          <Box mt={2}>
            <Button color="primary" component={HashLink} to="/reports#how_to">
              Read More
            </Button>
          </Box>
        </Grid>
        <Grid item={true} xs={12} sm={6} md={4}>
          <Box>
            <ActionImage src={progression_chart} alt="Progression Chart" />
          </Box>
          <Box mt={2}>
            <Typography
              variant="h6"
              gutterBottom={true}
              color="textPrimary"
              style={{ fontWeight: 700 }}
            >
              Progression charts
            </Typography>
            <Typography variant="body1" color="textSecondary">
              These charts show how a metric changes throughout the manuscript -
              hover over a particular point to see the first few words of the
              section it corresponds to. You can use this to find the weak spots
              in your manuscript and improve them.
            </Typography>
          </Box>
          <Box mt={2}>
            <Button color="primary" component={HashLink} to="/reports#how_to">
              Read More
            </Button>
          </Box>
        </Grid>
        <Grid item={true} xs={12} sm={6} md={4}>
          <Card
            elevation={4}
            style={{ backgroundColor: theme.palette.primary.main }}
          >
            <CardContent>
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom={true}
                  style={{ fontWeight: 700, color: theme.palette.common.white }}
                >
                  Learn More
                </Typography>
              </Box>
              <Box mt={2}>
                <Typography
                  variant="body1"
                  style={{ color: theme.palette.common.white }}
                >
                  Learn more about <Iris />' reports and how you can improve
                  your manuscripts.
                </Typography>
              </Box>
              <Box mt={6}>
                <Button
                  variant="outlined"
                  style={{
                    backgroundColor: theme.palette.common.white,
                    borderColor: theme.palette.common.white,
                    color: theme.palette.primary.main,
                  }}
                  fullWidth={true}
                  href="https://app.quantifiction.com/signup"
                  onClick={() => logEvent("sign_up", { place: "author-page" })}
                >
                  Sign Up Now
                </Button>
              </Box>
              <Box mt={1}>
                <Button
                  variant="outlined"
                  style={{
                    borderColor: theme.palette.common.white,
                    color: theme.palette.common.white,
                  }}
                  fullWidth={true}
                  component={HashLink}
                  to="/reports"
                >
                  Read Documentation
                </Button>
              </Box>
              <Box mt={1}>
                <Button
                  variant="outlined"
                  fullWidth={true}
                  style={{
                    borderColor: theme.palette.common.white,
                    color: theme.palette.common.white,
                  }}
                  href={SAMPLE_REPORT_URL}
                >
                  View Sample Report
                </Button>
              </Box>
              <Box mt={1}>
                <Button
                  variant="outlined"
                  style={{
                    borderColor: theme.palette.common.white,
                    color: theme.palette.common.white,
                  }}
                  fullWidth={true}
                  href={"mailto:" + SUPPORT_EMAIL}
                >
                  Contact Support
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export const BusinessCards = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"), {
    defaultMatches: true,
  });

  return (
    <Box my={8}>
      <Grid container={true} spacing={4} direction={isMd ? "row" : "column"}>
        <Grid item={true} xs={12} md={6}>
          <Box mt={4}>
            <BusinessCard
              author="Example Author"
              title="Example Script"
              score="73"
              sales="58"
              rating="4.1"
              genreFit="87"
              styleFit="92"
              disableCopy={true}
            />
          </Box>
        </Grid>
        <Grid item={true} xs={12} md={6}>
          <PageHeader
            overline="Book Business Cards"
            title="A Business Card For Your Manuscript"
            align="left"
          />
          <Typography variant="body1">
            Our <b>Book Business Card</b> feature allows you to export the most
            important metrics about your manuscript at a glance. You can
            generate your business card from your <Iris /> report and copy it
            into your emails and queries to publishers, agents or others. In
            turn, they will be directed to a dedicated page with further
            explanations if they want to know more.
            <Grid container={true} spacing={isMd ? 4 : 0}>
              <Grid item={true} xs={12} sm={6}>
                <List>
                  <ListItem>
                    <ListItemAvatar>
                      <CheckCircle color="action" />
                    </ListItemAvatar>
                    <ListItemText>Manuscript Score</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemAvatar>
                      <CheckCircle color="action" />
                    </ListItemAvatar>
                    <ListItemText>Sales Potential</ListItemText>
                  </ListItem>
                </List>
              </Grid>
              <Grid item={true} xs={12} sm={6}>
                <List>
                  <ListItem>
                    <ListItemAvatar>
                      <CheckCircle color="action" />
                    </ListItemAvatar>
                    <ListItemText>Genre Fit</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemAvatar>
                      <CheckCircle color="action" />
                    </ListItemAvatar>
                    <ListItemText>Style Fit</ListItemText>
                  </ListItem>
                </List>
              </Grid>
            </Grid>
            Of course, you can also export the full report and share it as you
            see fit.
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export const Privacy = () => {
  const theme = useTheme();

  return (
    <Box>
      <PageHeader
        title="Privacy First"
        subtitle={
          <>
            Publisher's and author's rights are of paramount importance to us,
            and we designed <Iris /> from the ground up with privacy in mind.
          </>
        }
      />
      <Box>
        <Grid container={true} spacing={4}>
          <Grid item={true} xs={12} md={6}>
            <Box
              height={48}
              width={40}
              marginBottom={2}
              px={0}
              borderBottom={`2px solid ${theme.palette.primary.main}`}
              textAlign="center"
            >
              <VisibilityOff color="primary" />
            </Box>
            <Typography
              variant="h5"
              gutterBottom={true}
              style={{ fontWeight: 700 }}
            >
              Account Separation
            </Typography>
            <Typography color="textSecondary">
              All our clients (i.e. individual publishers, authors, agents and
              editors) have separate, completely walled-off environments where
              their manuscripts are stored. No data is ever shared between
              accounts or released publicly, other than with the client's
              explicit consent.
            </Typography>
          </Grid>
          <Grid item={true} xs={12} md={6}>
            <Box
              height={48}
              width={40}
              marginBottom={2}
              px={0}
              borderBottom={`2px solid ${theme.palette.primary.main}`}
              textAlign="center"
            >
              <StopScreenShare color="primary" />
            </Box>
            <Typography
              variant="h5"
              gutterBottom={true}
              style={{ fontWeight: 700 }}
            >
              Data Sharing
            </Typography>
            <Typography color="textSecondary">
              <Link component={HashLink} to="/faq#provide_scripts">
                Unless you explicitely consent
              </Link>
              , we never use data from one client to train <Iris /> for another,
              nor is any data shared between clients.
            </Typography>
          </Grid>
          <Grid item={true} xs={12} md={6} spacing={4}>
            <Box
              height={48}
              width={40}
              marginBottom={2}
              px={0}
              borderBottom={`2px solid ${theme.palette.primary.main}`}
              textAlign="center"
            >
              <VoiceOverOff color="primary" />
            </Box>
            <Typography
              variant="h5"
              gutterBottom={true}
              style={{ fontWeight: 700 }}
            >
              Predictive AI
            </Typography>
            <Typography color="textSecondary">
              <Iris /> does not generate content, she only evaluates and
              predicts - her outputs are numbers, not text. As such,{" "}
              <i>by design</i>, she cannot reproduce or paraphrase texts. For
              these reasons, issues about LLMs and generative AI (i.e. ChatGPT)
              do not apply.
            </Typography>
          </Grid>
          <Grid item={true} xs={12} md={6} spacing={4}>
            <Box
              height={48}
              width={40}
              marginBottom={2}
              px={0}
              borderBottom={`2px solid ${theme.palette.primary.main}`}
              textAlign="center"
            >
              <Gavel color="primary" />
            </Box>
            <Typography
              variant="h5"
              gutterBottom={true}
              style={{ fontWeight: 700 }}
            >
              Responsible Use of Data
            </Typography>
            <Typography color="textSecondary">
              To train <Iris /> we're only using public sources and strictly
              adhere to applicable licenses and copyrights. And since we don't
              use henerative AI, it is impossible to reverse-engineer <Iris />'
              training data to reproduce a published novel or draft manuscript.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AuthorPage;



// WEBPACK FOOTER //
// ./src/pages/AuthorPage.tsx